import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/Landing.css';
import { Icon } from '../components/ui';
import fidarDark from '../images/Agenti5_dark_bck.png';
import fidarLight from '../images/Agenti5_light_bck.png';
import { useTranslation } from 'react-i18next';

interface LandingProps {
  isDarkMode: boolean;
  onToggleDarkMode: () => void;
}

const getFEATURES = (t: (key: string) => string) => [
  {
    icon: 'shield' as const,
    title: t('pages.landing.zeroTrustSecurity'),
    desc: t('pages.landing.zeroTrustSecurityDesc'),
  },
  {
    icon: 'users' as const,
    title: t('pages.landing.identityManagement'),
    desc: t('pages.landing.identityManagementDesc'),
  },
  {
    icon: 'bolt' as const,
    title: t('pages.landing.realTimeMonitoring'),
    desc: t('pages.landing.realTimeMonitoringDesc'),
  },
  {
    icon: 'logs' as const,
    title: t('pages.landing.fullAuditTrails'),
    desc: t('pages.landing.fullAuditTrailsDesc'),
  },
  {
    icon: 'server' as const,
    title: t('pages.landing.mcpGateway'),
    desc: t('pages.landing.mcpGatewayDesc'),
  },
  {
    icon: 'bell' as const,
    title: t('pages.landing.smartViolations'),
    desc: t('pages.landing.smartViolationsDesc'),
  },
];

const Landing: React.FC<LandingProps> = ({ isDarkMode, onToggleDarkMode }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Animated particle background
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animId: number;
    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const particles = Array.from({ length: 60 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      r: Math.random() * 2 + 0.5,
      dx: (Math.random() - 0.5) * 0.4,
      dy: (Math.random() - 0.5) * 0.4,
      opacity: Math.random() * 0.5 + 0.1,
    }));

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach(p => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = isDarkMode
          ? `rgba(255,255,255,${p.opacity})`
          : `rgba(18,18,18,${p.opacity * 0.6})`;
        ctx.fill();
        p.x += p.dx;
        p.y += p.dy;
        if (p.x < 0 || p.x > canvas.width) p.dx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.dy *= -1;
      });

      // Connect nearby particles
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dist = Math.hypot(particles[i].x - particles[j].x, particles[i].y - particles[j].y);
          if (dist < 100) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = isDarkMode
              ? `rgba(255,255,255,${0.08 * (1 - dist / 100)})`
              : `rgba(18,18,18,${0.06 * (1 - dist / 100)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }
      animId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, [isDarkMode]);

  return (
    <div className={`lp-root${isDarkMode ? '' : ' lp-light'}`}>
      {/* Animated background */}
      <canvas ref={canvasRef} className="lp-canvas" />

      {/* Navbar */}
      <nav className="lp-nav">
        <div className="lp-nav-brand">
          <img
            src={isDarkMode ? fidarDark : fidarLight}
            alt={t('pages.landing.fidar')}
            className="lp-nav-logo"
          />
        </div>
        <div className="lp-nav-actions">
          <button
            className="lp-theme-toggle"
            onClick={onToggleDarkMode}
            aria-label={isDarkMode ? t('pages.landing.switchToLightMode') : t('pages.landing.switchToDarkMode')}
          >
            <Icon name={isDarkMode ? 'sun' : 'moon'} size={18} />
          </button>
          <button className="lp-nav-signin" onClick={() => navigate('/login')}>
            {t('pages.landing.signIn')}
          </button>
        </div>
      </nav>

      {/* Hero */}
      <section className="lp-hero">
        <div className="lp-badge"><span>{t('pages.landing.zeroTrustAIPlatform')}</span></div>
        <h1 className="lp-title">
          {t('pages.landing.secureEveryAIInteraction')}
          <span className="lp-title-accent"> {t('pages.landing.atScale')}</span>
        </h1>
        <p className="lp-lead">
          {t('pages.landing.agenti5EnforcesZeroTrustPoliciesAcross')}
        </p>
        <div className="lp-cta-group">
          <button className="lp-cta-primary" onClick={() => navigate('/login')}>
            {t('pages.landing.signIn')}
            <Icon name="chevronRight" size={18} />
          </button>
        </div>
      </section>

      {/* Features */}
      <section className="lp-features">
        <h2 className="lp-section-title">{t('pages.landing.everythingYouNeedToStaySecure')}</h2>
        <div className="lp-features-grid">
          {getFEATURES(t).map((f, i) => (
            <div key={f.title} className="lp-feature-card" style={{ animationDelay: `${i * 0.08}s` }}>
              <div className="lp-feature-icon">
                <Icon name={f.icon} size={22} />
              </div>
              <h3>{f.title}</h3>
              <p>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA footer band */}
      <section className="lp-band">
        <h2>{t('pages.landing.readyToSecureYourAIInfrastructure')}</h2>
        <button className="lp-cta-primary" onClick={() => navigate('/login')}>
          {t('pages.landing.signInToDashboard')}
          <Icon name="chevronRight" size={18} />
        </button>
      </section>

      <footer className="lp-footer">
        <p>{t('pages.landing.2026Agenti5PlatformEnterpriseSecuritySolutions')}</p>
      </footer>
    </div>
  );
};

export default Landing;
